<script setup lang="ts">
import { onMounted, ref, computed, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { usePageStore } from "@/stores/pageStore";
import { usePageSEO } from '@/composables/usePageSEO';
import {useFormatStore} from "@/stores/formatStore";
import { useI18n } from 'vue-i18n';
import CustomSpinner from "@/components/CustomSpinner/CustomSpinner.vue";

const { t } = useI18n();
// usePageSEO('blog');

const pageStore = usePageStore();
const formatStore = useFormatStore();

const route = useRoute();
const router = useRouter();

const slug = "travel-inspiration";
const pageSize = 9;
const currentPage = ref<number>(Number(route.query.page ?? 1));

const totalPages = computed<number>(() => (pageStore.pages ?? 1));

function goToPage(page: number) {
  if (page < 1 || page > totalPages.value) return;
  router.replace({ query: { ...route.query, page: String(page) } });
}

onMounted(async () => {
  await pageStore.getAll(slug, currentPage.value, pageSize, 'cosmosy', { splitHero: false });
});

watch(
  () => route.query.page,
  async (val) => {
    currentPage.value = Number(val ?? 1);
    await pageStore.getAll(slug, currentPage.value, pageSize, 'cosmosy', { splitHero: false });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
);
</script>

<template>
  <div class="container pb-12">
    <h1 class="self-start responsive-title-1 max-md:max-w-full pt-24">
      {{ t('travelInspiration.title') }}
    </h1>

    <div
        class="mt-8 mb-20 responsive-body-large text-black mix-blend-multiply max-md:max-w-full"
        v-html="t('travelInspiration.body')"
    />
    <!-- Loader -->
    <div v-if="pageStore.loading" class="flex justify-center items-center min-h-[300px]">
      <CustomSpinner/>
    </div>

    <!-- Blog Posts -->
    <div
        v-else
        class="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
    >
      <div
          v-for="page in pageStore.pageList"
          :key="page.id"
          class="flex flex-col gap-4  bg-white rounded-lg transition-transform transform md:hover:scale-[1.01]"
      >
        <!-- Image -->
        <router-link :to="`/page/${slug}/${page.slug}`">
          <img
              :src="page.imageSet['760w']"
              :alt="page.title"
              class="w-full h-[200px] md:h-[240px] object-cover rounded-2xl"
          />
        </router-link>

        <!-- Content -->
        <div class=" flex flex-col h-full">
          <div class="flex flex-col gap-2 flex-1">
            <span class="text-sm text-gray-500">
              {{
                formatStore.formatDateWithCustomPatternMapping(
                    page.publishedAt,
                    "MMM DD, YYYY"
                )
              }}
            </span>
            <router-link :to="`/page/${slug}/${page.slug}`" class="text-lg md:text-xl font-semibold line-clamp-2 hover:underline">
              {{ page.title }}
            </router-link>
            <span class="text-sm md:text-base text-gray-700 line-clamp-3">
              {{ page.seoDescription }}
            </span>
          </div>

          <!-- Button -->
          <router-link
              :to="`/page/${slug}/${page.slug}`"
              class="bg-primary-blue w-fit rounded-2xl px-5 py-2 flex shadow-md transition transform hover:scale-105"
          >
            <img
                src="@/assets/icons/svg/Arrow_right_white_16x16.svg"
                alt="arrow"
                class="w-4 h-4"
            />
          </router-link>
        </div>
      </div>
    </div>
    <!-- Pagination -->
    <div v-if="totalPages > 1" class="mt-20 flex items-center justify-center gap-2">
      <button
        class="btn px-4 py-1.5 rounded-xl border disabled:opacity-50 shadow-md transition transform hover:scale-105"
        :disabled="currentPage === 1"
        @click="goToPage(currentPage - 1)"
      >
        {{ 'Previous' }}
      </button>

      <button
        v-for="n in totalPages"
        :key="`page-${n}`"
        class="px-3 py-1 rounded-xl border transition hover:shadow"
        :class="n === currentPage ? 'bg-primary-blue text-white' : 'bg-white'"
        @click="goToPage(n)"
      >
        {{ n }}
      </button>

      <button
        class="btn px-3 py-1 rounded-xl border disabled:opacity-50 shadow-md transition transform hover:scale-105"
        :disabled="currentPage === totalPages"
        @click="goToPage(currentPage + 1)"
      >
        {{'Next' }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.btn {
  background-color: #007bff;
  color: white;
}

</style>
